/* eslint-disable @typescript-eslint/no-explicit-any */

import type {
  BaseErrorResponse,
  CreatePaymentReq,
  CreatePaymentRes,
  EmptySuccessResponse,
  GetCatalogRes,
  GetPosDocumentItemsRes,
  GetPosDocumentStatusesRes,
  GetPosDocumentTypesRes,
  GetPosDocumentsRes,
  GetPosListRes,
  GetPosWarehousesRes,
  GetProductRes,
  PaymentDataReq,
} from './data-contracts';
import type { RequestParams } from './http-client';
import { ContentType, HttpClient } from './http-client';


export default function usePos() {
  class Pos<SecurityDataType = unknown> extends HttpClient<SecurityDataType> {
    /**
     * @description Please use /v2/pos/product instead
     *
     * @tags POS
     * @name CatalogControllerGetCatalog
     * @summary Getting a product catalog
     * @request GET:/api3/pos/catalog
     * @deprecated
     * @response `200` `GetCatalogRes`
     * @response `400` `BaseErrorResponse`
     */
    catalogControllerGetCatalog = (
      query: {
        /**
         * Requested categories (Must be an integer or a comma-separated sequence of integers)
         * @example "1,2,3"
         */
        catalogid?: string;
        /**
         * Exclude categories from the sample (Must be an integer or a comma-separated sequence of integers)
         * @example "1,2,3"
         */
        excatalogid?: string;
        /**
         * @min 1
         * @example 1
         */
        pricelistid?: any;
        /**
         * @min 1
         * @example 1
         */
        posid?: any;
        /** @example "ASC" */
        articleOrder: string;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetCatalogRes, BaseErrorResponse>({
        path: '/api3/pos/catalog',
        method: 'GET',
        query,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags POS
     * @name PosDocumentControllerGetPosDocuments
     * @summary Getting a pos documents
     * @request GET:/api3/pos/document
     * @secure
     * @response `200` `GetPosDocumentsRes`
     * @response `400` `BaseErrorResponse`
     */
    posDocumentControllerGetPosDocuments = (
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        /** @example 1 */
        documentTypeId?: number;
        /** @example 1 */
        documentStatusId?: number;
        /** @example 1 */
        sourcePosId?: number;
        /** @example 1 */
        targetPosId?: number;
        /**
         * Sort options
         * @example {"orderBy":{"pos-document.id":"ASC","pos-document.number":"DESC"}}
         */
        orderBy?: object;
        /**
         * Filtering parameters
         * @example {"filter":[{"field":"pos-document.id","value":"1","operation":">"},{"field":"pos-document.number","value":"0000","operation":"iLike"}]}
         */
        filter?: object;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetPosDocumentsRes, BaseErrorResponse>({
        path: '/api3/pos/document',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags POS
     * @name PosDocumentControllerGetPosDocumentItems
     * @summary Getting sorted document's items
     * @request GET:/api3/pos/document/{id}/items
     * @secure
     * @response `200` `GetPosDocumentItemsRes`
     * @response `400` `BaseErrorResponse`
     */
    posDocumentControllerGetPosDocumentItems = (id: any, params: RequestParams = {}) =>
      this.request<GetPosDocumentItemsRes, BaseErrorResponse>({
        path: `/api3/pos/document/${id}/items`,
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags POS
     * @name PosDocumentTypeControllerGetPosDocuments
     * @summary Getting a pos document types
     * @request GET:/api3/pos/document-type
     * @secure
     * @response `200` `GetPosDocumentTypesRes`
     * @response `400` `BaseErrorResponse`
     */
    posDocumentTypeControllerGetPosDocuments = (
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        /** @example 1 */
        documentTypeId?: number;
        /** @example 1 */
        documentStatusId?: number;
        /** @example 1 */
        sourcePosId?: number;
        /** @example 1 */
        targetPosId?: number;
        /**
         * Sort options
         * @example {"orderBy":{"pos-document.id":"ASC","pos-document.number":"DESC"}}
         */
        orderBy?: object;
        /**
         * Filtering parameters
         * @example {"filter":[{"field":"pos-document.id","value":"1","operation":">"},{"field":"pos-document.number","value":"0000","operation":"iLike"}]}
         */
        filter?: object;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetPosDocumentTypesRes, BaseErrorResponse>({
        path: '/api3/pos/document-type',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags POS
     * @name ListControllerGetPosList
     * @summary Getting a list of warehouses
     * @request GET:/api3/pos/list
     * @secure
     * @response `200` `GetPosListRes`
     * @response `400` `BaseErrorResponse`
     */
    listControllerGetPosList = (
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        /**
         * Self-delivery is available
         * @example true
         */
        only_pickup_available?: boolean;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetPosListRes, BaseErrorResponse>({
        path: '/api3/pos/list',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags POS
     * @name DocumentStatusControllerGetDocumentStatuses
     * @summary Returns a list of order document statuses
     * @request GET:/api3/pos/order/document-status
     * @secure
     * @response `200` `GetPosDocumentStatusesRes`
     * @response `400` `BaseErrorResponse`
     */
    documentStatusControllerGetDocumentStatuses = (params: RequestParams = {}) =>
      this.request<GetPosDocumentStatusesRes, BaseErrorResponse>({
        path: '/api3/pos/order/document-status',
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags POS
     * @name PosOrderPaymentControllerCreatePayment
     * @summary Creating an order payment
     * @request POST:/api3/pos/order/{documentId}/payment
     * @secure
     * @response `200` `CreatePaymentRes`
     * @response `400` `BaseErrorResponse`
     */
    posOrderPaymentControllerCreatePayment = (documentId: number, data: CreatePaymentReq, params: RequestParams = {}) =>
      this.request<CreatePaymentRes, BaseErrorResponse>({
        path: `/api3/pos/order/${documentId}/payment`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags POS
     * @name PosOrderPaymentControllerCreatePaymentFromWallet
     * @summary Creating an order payment from wallet
     * @request POST:/api3/pos/order/{documentId}/payment/wallet/{walletId}
     * @secure
     * @response `200` `CreatePaymentRes`
     * @response `400` `BaseErrorResponse`
     */
    posOrderPaymentControllerCreatePaymentFromWallet = (
      documentId: number,
      walletId: number,
      data: PaymentDataReq,
      params: RequestParams = {},
    ) =>
      this.request<CreatePaymentRes, BaseErrorResponse>({
        path: `/api3/pos/order/${documentId}/payment/wallet/${walletId}`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags POS
     * @name PosOrderPaymentControllerDeletePayment
     * @summary Deleting an order payment
     * @request DELETE:/api3/pos/order/{documentId}/payment/{paymentId}
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    posOrderPaymentControllerDeletePayment = (paymentId: number, documentId: string, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/pos/order/${documentId}/payment/${paymentId}`,
        method: 'DELETE',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags POS
     * @name PosProductControllerGetCatalog
     * @summary Getting information about the product
     * @request GET:/api3/pos/product/{id}
     * @response `200` `GetProductRes`
     * @response `400` `BaseErrorResponse`
     */
    posProductControllerGetCatalog = (
      id: any,
      query?: {
        /**
         * Price list identifier
         * @min 1
         * @example 1
         */
        pricelistid?: any;
        /**
         * Warehouse ID
         * @min 1
         * @example 1
         */
        posid?: any;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetProductRes, BaseErrorResponse>({
        path: `/api3/pos/product/${id}`,
        method: 'GET',
        query,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags POS
     * @name PosWarehouseControllerGetAccounts
     * @summary Returns a list of warehouses
     * @request GET:/api3/pos/warehouse
     * @secure
     * @response `200` `GetPosWarehousesRes`
     * @response `400` `BaseErrorResponse`
     */
    posWarehouseControllerGetAccounts = (params: RequestParams = {}) =>
      this.request<GetPosWarehousesRes, BaseErrorResponse>({
        path: '/api3/pos/warehouse',
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });
  }

  return new Pos();
}
